
    import __i18nConfig from '@next-translate-root/i18n.js'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import Layout from "@components/layout/Layout";
import DynamicZone from "@components/strapi/DynamicZone";
import BreadcrumbSection from "@components/strapi/sections/BreadcrumbSection";
import { PageProps, pageHasAside, pageIsBandLayout } from "@lib/frontend/pages/webPage";
import EntityDetail, { EntityDetailProps } from "@components/layout/pages/EntityDetail";
import MedicalTherapyJsonLd from "@components/utils/medical-therapy-jsonld";
/**
 * Renders a CMS-driven page from its zones.
 *
 * Three bands, in order:
 *   headingZone      — full width, above everything (page heading, hero)
 *   content + aside  — the boxed reading column, optionally with a sidebar
 *   bottomSections   — full width, below (the CTA band, the footer's neighbour)
 *
 * Why the columns are decided HERE and not by the sections: a section that
 * draws its own page-wide grid cannot be placed beside a sibling, and a section
 * that draws its own band makes itself the only thing that can live in it.
 * Both were real problems on the sibling project. The page owns the layout;
 * sections render their content and nothing else.
 *
 * Layout is boxed at `shell` with prose capped near 65ch (item 4) — the old
 * site ran body copy the full width of the browser, which is the single
 * biggest reason it read as hard work.
 */
export default function ModularPage(props: PageProps) {
    const hasAside = pageHasAside(props);
    const isBand = pageIsBandLayout(props);
    const parentProps = { ...props, sectionsData: props.sectionsData };
    return (<Layout {...props}>
      {props.headingZone?.length ? (<div className="w-full">
          <DynamicZone components={props.headingZone} parentProps={parentProps}/>
        </div>) : null}

      {props.breadcrumb ? (
        // rendered directly, NOT through DynamicZone: the breadcrumb is a
        // single component attribute, and the service DTO carries no
        // __component discriminator — the dispatcher would (and did) resolve
        // no renderer and silently drop it
        <div className="shell pt-4">
          <BreadcrumbSection section={props.breadcrumb as never} page={parentProps}/>
        </div>) : null}

      <div className={isBand ? "bg-violet-050 py-10 md:py-14" : ""}>
        <div className="shell">
          <div className={hasAside
            ? "grid grid-cols-1 gap-10 py-10 md:py-14 lg:grid-cols-[minmax(0,1fr)_260px] lg:gap-12"
            : "py-10 md:py-14"}>
            {/* first section: its rhythm margin (my-14/20) is for BETWEEN
            sections — against the page header it reads as dead air
            (owner, 2026-09-10, listing pages) */}
            <div className={`${hasAside ? "min-w-0 " : ""}[&>section:first-child]:mt-6 md:[&>section:first-child]:mt-8`}>
              {(() => {
            const { entity, entityKind } = props as unknown as {
                entity?: EntityDetailProps["entity"];
                entityKind?: EntityDetailProps["kind"] | "post";
            };
            return entity && (entityKind === "treatment" || entityKind === "condition" || entityKind === "team") ? (<>
                    {entityKind === "treatment" ? (<MedicalTherapyJsonLd entity={entity}/>) : null}
                    <EntityDetail entity={entity} kind={entityKind}/>
                  </>) : null;
        })()}
              <DynamicZone components={props.contentSections} parentProps={parentProps}/>
            </div>

            {hasAside ? (
        // The sidebar is not a dead "see also" list: on long pages it
        // indexes the Q&A headings the copy already uses and keeps the
        // booking card in view the whole way down. top-24 clears the
        // sticky header; with top-4 the card tops slid underneath it.
        // Capped at viewport height + internal scroll (owner, 2026-09-10):
        // on long pages the sticky aside outgrows the screen and its
        // bottom cards were unreachable until the main content ended.
        // Scrollbar hidden on owner request — wheel/trackpad still
        // scrolls, the cards just stay clean. Contained overscroll so
        // hitting the end doesn't yank the page.
        <aside className="lg:sticky lg:top-24 lg:max-h-[calc(100vh-7rem)] lg:self-start lg:overflow-y-auto lg:overscroll-contain lg:[scrollbar-width:none] lg:[&::-webkit-scrollbar]:hidden">
                <DynamicZone components={props.asideSections} parentProps={parentProps}/>
              </aside>) : null}
          </div>
        </div>
      </div>

      {props.bottomSections?.length ? (<div className="w-full">
          <DynamicZone components={props.bottomSections} parentProps={parentProps}/>
        </div>) : null}
    </Layout>);
}

    async function __Next_Translate__getStaticProps__1a09d39691a__(ctx) {
      if (typeof global !== 'undefined') {
        global.i18nConfig = __i18nConfig
      }
      
      return {
        
        
        props: {
          
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: 'components/layout//ModularPage',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || (() => Promise.resolve({})),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a09d39691a__ as getStaticProps }
  